import {
  GetCategoriesResponse,
  GetChatMessagesForm,
  GetChatMessagesResponse,
  GetChatsForm,
  GetChatsResponse,
  GetDevMessagesResponse,
  QNAResponseStreaming,
  QNAStreamingForm,
} from '../../interfaces/api/qna';
import { api } from '../api';
import http from '../http';

/**
 * Question and answer service
 * Provides methods for question answering, chat history, and related functionality
 */
export const qna = {
  /**
   * Retrieves available chat categories
   * @returns {Promise<GetCategoriesResponse>} Promise resolving to categories data
   */
  getCategories: (): Promise<GetCategoriesResponse> =>
    new Promise((resolve, reject) => {
      http
        .get(api.qna.categories)
        .then((res) => resolve(res as GetCategoriesResponse))
        .catch((e) => reject(e));
    }),

  /**
   * Retrieves a list of chats with optional filtering
   * @param {GetChatsForm} [body] - Optional parameters for filtering chats
   * @returns {Promise<GetChatsResponse>} Promise resolving to chat list response
   */
  getChats: (body?: GetChatsForm): Promise<GetChatsResponse> =>
    new Promise((resolve, reject) => {
      const url = `${api.qna.chats}?length=${body?.length || 50}&start=${body?.start || 0}&search=${body?.search || ''}`;
      http
        .get(url)
        .then((res) => resolve(res as GetChatsResponse))
        .catch((e) => reject(e));
    }),

  /**
   * Retrieves messages for a specific chat
   * @param {GetChatMessagesForm} body - Contains chat ID to get messages for
   * @returns {Promise<GetChatMessagesResponse>} Promise resolving to chat messages
   */
  getChatMessages: (body: GetChatMessagesForm): Promise<GetChatMessagesResponse> =>
    new Promise((resolve, reject) => {
      const url = `${api.qna.chatMessages}/${body.id}?all=1`;
      http
        .get(url)
        .then((res) => resolve(res as GetChatMessagesResponse))
        .catch((e) => reject(e));
    }),

  /**
   * Sends a question to the AI and receives a streaming response
   * @param {QNAStreamingForm} body - Question data including context, history, etc.
   * @returns {Promise<QNAResponseStreaming>} Promise resolving to streaming response
   */
  sendQNAStreaming: (body: QNAStreamingForm): Promise<QNAResponseStreaming> =>
    new Promise((resolve, reject) => {
      http
        .postStreaming(api.qna.askStreaming, body)
        .then((res) => resolve(res as QNAResponseStreaming))
        .catch((e) => reject(e));
    }),

  /**
   * Retrieves developer messages for a specific chat
   * @param {number|string} id - ID of the chat to get developer messages for
   * @returns {Promise<GetDevMessagesResponse>} Promise resolving to developer messages
   */
  getDevMessages: (id: number | string): Promise<GetDevMessagesResponse> =>
    new Promise((resolve, reject) => {
      const url = `${api.qna.devMessages}/${id}`;
      http
        .get(url)
        .then((res) => resolve(res as GetDevMessagesResponse))
        .catch((e) => reject(e));
    }),
};
